import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CHAT_MAX_CHARS, type ChatMessageDto, type ChatTurnPending, type HabitInput, type TaskInput } from "@habitos/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAiStatus } from "../lib/queries";
import { PlanReview } from "../components/PlanReview";
import { TaskReview } from "../components/TaskReview";
import { EditReview } from "../components/EditReview";

/** Longer messages (a pasted plan) show folded in the log, with a button to open them. */
const FOLD_CHARS = 1200;

const STARTERS = [
  "Ayúdame a organizar mi día de hoy",
  "Mañana tengo muchas cosas que hacer",
  "Quiero una rutina de mañana más tranquila",
  "Tengo una meta grande y no sé por dónde empezar",
  "Quiero retomar el ejercicio",
  "Ayúdame a ordenar mi semana",
];

export function Assistant() {
  const { user } = useAuth();
  const client = useQueryClient();
  const ai = useAiStatus();
  const messages = useQuery({ queryKey: ["chat"], queryFn: api.chat });
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  const progress = (text: string) => (turn: ChatTurnPending) =>
    setPending({ text, startedAt: Date.parse(turn.startedAt), attempt: turn.attempt, part: turn.part });
  const answered = async () => {
    await client.invalidateQueries({ queryKey: ["chat"] });
    setPending(null);
  };

  const send = useMutation({
    mutationFn: (text: string) => api.sendChat(text, progress(text)),
    onMutate: (text) => {
      setError(null);
      setPending({ text, startedAt: Date.now(), attempt: 1, part: 1 });
      setDraft("");
    },
    onSuccess: answered,
    onError: (e, text) => {
      setError((e as Error).message);
      setPending(null);
      setDraft(text);
    },
  });

  // Back on the chat while an answer is still being written (they left, or came from "Tu semana").
  const resume = useMutation({
    mutationFn: (turn: ChatTurnPending) => api.resumeChat(turn, progress(turn.text)),
    onMutate: (turn) => progress(turn.text)(turn),
    onSuccess: answered,
    onError: (e) => {
      setError((e as Error).message);
      setPending(null);
    },
  });
  const busy = send.isPending || resume.isPending;

  useEffect(() => {
    void api
      .currentChatTurn()
      .then((turn) => turn && resume.mutate(turn))
      .catch(() => undefined);
    // Once, when the chat opens.
  }, []);

  const accept = useMutation({
    mutationFn: (v: { id: string; habits: HabitInput[] }) => api.acceptChatPlan(v.id, v.habits),
    onSuccess: () =>
      Promise.all(["chat", "goals", "habits"].map((key) => client.invalidateQueries({ queryKey: [key] }))),
    onError: (e) => setError((e as Error).message),
  });

  const acceptEdits = useMutation({
    mutationFn: (v: { id: string; indexes: number[] }) => api.acceptChatEdits(v.id, v.indexes),
    onSuccess: () =>
      Promise.all(["chat", "tasks", "habits", "goals"].map((key) => client.invalidateQueries({ queryKey: [key] }))),
    onError: (e) => setError((e as Error).message),
  });

  const acceptTasks = useMutation({
    mutationFn: (v: { id: string; tasks: TaskInput[] }) => api.acceptChatTasks(v.id, v.tasks),
    onSuccess: () => Promise.all(["chat", "tasks"].map((key) => client.invalidateQueries({ queryKey: [key] }))),
    onError: (e) => setError((e as Error).message),
  });

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.data?.length, pending]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (text && !busy) send.mutate(text);
  }

  async function restart() {
    if (!confirm("¿Empezar una conversación nueva? Tus planes guardados no se borran.")) return;
    await api.clearChat();
    await client.invalidateQueries({ queryKey: ["chat"] });
  }

  if (ai.data && !ai.data.enabled) {
    return <p className="banner">La IA todavía no está activada en este servidor.</p>;
  }

  const list = messages.data ?? [];

  return (
    <div className="chat">
      <header className="page-header row">
        <h1>Asistente</h1>
        {list.length > 0 && <button className="btn" onClick={restart}>Nueva charla</button>}
      </header>

      <div className="chat-log">
        <Bubble role="assistant">
          Hola, {user.name}. Cuéntame qué tienes que hacer hoy o en los próximos días, o qué te gustaría cambiar o
          lograr, y lo acomodamos en tu calendario.
        </Bubble>
        {list.length === 0 && !pending && (
          <div className="chips starters">
            {STARTERS.map((s) => (
              <button key={s} className="chip" disabled={busy} onClick={() => send.mutate(s)}>{s}</button>
            ))}
          </div>
        )}
        {list.map((m) => (
          <Message
            key={m.id}
            message={m}
            saving={
              (accept.isPending && accept.variables?.id === m.id) ||
              (acceptTasks.isPending && acceptTasks.variables?.id === m.id) ||
              (acceptEdits.isPending && acceptEdits.variables?.id === m.id)
            }
            onAccept={(habits) => accept.mutate({ id: m.id, habits })}
            onAcceptTasks={(tasks) => acceptTasks.mutate({ id: m.id, tasks })}
            onAcceptEdits={(indexes) => acceptEdits.mutate({ id: m.id, indexes })}
            onRetry={() => !busy && send.mutate("¿Me propones otra opción?")}
          />
        ))}
        {pending && (
          <>
            <Bubble role="user"><MessageText text={pending.text} /></Bubble>
            <Working pending={pending} />
          </>
        )}
        {error && <p className="error">{error}</p>}
        <div ref={bottom} />
      </div>

      <form className="chat-input" onSubmit={onSubmit}>
        {draft.length > FOLD_CHARS && (
          <p className={`muted chat-count${draft.length >= CHAT_MAX_CHARS ? " error" : ""}`}>
            {draft.length.toLocaleString("es")} / {CHAT_MAX_CHARS.toLocaleString("es")} caracteres
          </p>
        )}
        <textarea
          className="textarea"
          rows={1}
          value={draft}
          maxLength={CHAT_MAX_CHARS}
          placeholder="Escribe aquí…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSubmit(e);
            }
          }}
        />
        <button className="btn primary" disabled={busy || !draft.trim()} aria-label="Enviar">➤</button>
      </form>
    </div>
  );
}

/** The message being answered and how the answer is going. */
interface Pending {
  text: string;
  startedAt: number;
  attempt: number;
  part: number;
}

/** Above this, the message is a pasted plan and the wait deserves an explanation up front. */
const LONG_MESSAGE_CHARS = 5000;

/** The typing dots, plus what's going on and for how long once the wait gets noticeable. */
function Working({ pending }: { pending: Pending }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.floor((now - pending.startedAt) / 1000));
  const long = pending.text.length > LONG_MESSAGE_CHARS;
  const status =
    pending.part > 1
      ? `Armando la propuesta: parte ${pending.part}…`
      : pending.attempt > 1
      ? "La IA estaba ocupada; ahora lo está intentando con otro modelo…"
      : long
        ? seconds < 30
          ? "Leyendo tu plan…"
          : "Armando la propuesta…"
        : "Pensando…";
  return (
    <div className="working" aria-live="polite">
      <div className="bubble assistant typing" aria-label="Escribiendo">
        <span />
        <span />
        <span />
      </div>
      {(long || seconds >= 5 || pending.attempt > 1 || pending.part > 1) && (
        <p className="muted small">
          {status} {seconds >= 5 && <span className="working-time">{formatElapsed(seconds)}</span>}
        </p>
      )}
      {(long || seconds >= 20) && (
        <p className="muted small">
          Con planes largos puede tardar un par de minutos. Puedes salir de esta pantalla: la respuesta te espera aquí.
        </p>
      )}
    </div>
  );
}

function formatElapsed(seconds: number) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function Bubble({ role, children }: { role: "user" | "assistant"; children: ReactNode }) {
  return <div className={`bubble ${role}`}>{children}</div>;
}

/** A message's text, folded when it is long so a pasted plan doesn't bury the conversation. */
function MessageText({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  if (text.length <= FOLD_CHARS) return <>{text}</>;
  return (
    <>
      {open ? text : `${text.slice(0, FOLD_CHARS).trimEnd()}…`}
      <button type="button" className="bubble-more" onClick={() => setOpen(!open)}>
        {open ? "Ver menos" : `Ver todo (${text.length.toLocaleString("es")} caracteres)`}
      </button>
    </>
  );
}

function Message(props: {
  message: ChatMessageDto;
  saving: boolean;
  onAccept: (habits: HabitInput[]) => void;
  onAcceptTasks: (tasks: TaskInput[]) => void;
  onAcceptEdits: (indexes: number[]) => void;
  onRetry: () => void;
}) {
  const { message } = props;
  return (
    <>
      <Bubble role={message.role}><MessageText text={message.text} /></Bubble>
      {message.editProposal &&
        (message.proposalStatus === "accepted" ? (
          <Link to="/" className="banner">✓ Aplicaste estos cambios. Ya se ven en tu calendario.</Link>
        ) : (
          <div className="chat-plan">
            <EditReview
              proposal={message.editProposal}
              busy={props.saving}
              onAccept={props.onAcceptEdits}
              onRetry={props.onRetry}
            />
          </div>
        ))}
      {message.taskProposal &&
        (message.proposalStatus === "accepted" ? (
          <Link to="/" className="banner">✓ Guardaste estas tareas. Las ves en Hoy, cada una en su día.</Link>
        ) : (
          <div className="chat-plan">
            <TaskReview
              proposal={message.taskProposal}
              busy={props.saving}
              onAccept={props.onAcceptTasks}
              onRetry={props.onRetry}
            />
          </div>
        ))}
      {message.proposal &&
        (message.proposalStatus === "accepted" ? (
          <Link to="/metas" className="banner">✓ Guardaste “{message.proposal.goalTitle}”. Lo ves en Metas y en Hoy.</Link>
        ) : (
          <div className="chat-plan">
            <h2 className="section" style={{ marginTop: 0 }}>{message.proposal.goalTitle}</h2>
            <PlanReview
              proposal={message.proposal}
              acceptLabel="Guardar en mi calendario"
              busy={props.saving}
              onAccept={props.onAccept}
              onRetry={props.onRetry}
            />
          </div>
        ))}
    </>
  );
}
